import { fetchProject } from '@/lib/api/projects'
import { apiClient } from '@/lib/api/base'
import { supabase } from '@/lib/supabase'
import type { WhiteSpaceCandidate } from '@/types/project'

type StepId = 'step0' | 'step1' | 'step2' | 'step3' | 'step4' | 'step5' | 'step6' | 'step7' | 'step8' | 'step9' | 'step10' | 'step11'

export type StepData = Record<string, any>

// Step 9: White Space Opportunities (Evaluated Candidates with Strategic Scores)
export interface Step9WhiteSpaceData {
  evaluatedWhiteSpaces: WhiteSpaceCandidate[]  // Only candidates with strategic_score
  selectedWhiteSpace?: {
    x_dimension_key: string
    y_dimension_key: string
    quadrant: string
  }
}

export interface ProjectSession {
  projectId: string
  steps: Record<StepId, StepData | null>
  stepFinalizationStatus?: Record<number, boolean>  // Tracks which steps have been finalized
  updatedAt?: string
  // Store full project details for components that need metadata, permissions, etc.
  project?: any
}

type Listener = (session: ProjectSession | null) => void

const stepNumberToKey = (num: number): StepId => `step${num}` as StepId

class SessionStore {
  private cache: Map<string, ProjectSession> = new Map()
  private listeners: Map<string, Set<Listener>> = new Map()
  private inflightRefresh: Map<string, Promise<ProjectSession>> = new Map()
  private realtimeSubscriptions: Map<string, { unsubscribe: () => void }> = new Map()

  getSnapshot(projectId: string): ProjectSession | null {
    return this.cache.get(projectId) || null
  }

  /**
   * Update step data in the cache without triggering a full refresh.
   * This is useful for caching data that isn't part of the main project fetch.
   */
  updateStepData(projectId: string, stepKey: StepId, data: Partial<StepData>): void {
    const existing = this.cache.get(projectId)
    if (!existing) {
      console.warn(`[sessionStore] Cannot update step data: no session for project ${projectId}`)
      return
    }
    
    const existingStepData = existing.steps[stepKey] || {}
    const updatedSession: ProjectSession = {
      ...existing,
      steps: {
        ...existing.steps,
        [stepKey]: {
          ...existingStepData,
          ...data
        }
      },
      updatedAt: new Date().toISOString()
    }
    
    this.cache.set(projectId, updatedSession)
    this.emit(projectId)
    console.log(`[sessionStore] Updated ${stepKey} cache for project ${projectId}`)
  }

  subscribe(projectId: string, cb: Listener): () => void {
    if (!this.listeners.has(projectId)) this.listeners.set(projectId, new Set())
    const set = this.listeners.get(projectId)!
    set.add(cb)
    // emit current on subscribe
    cb(this.getSnapshot(projectId))
    // ensure realtime attached
    this.ensureRealtime(projectId)
    return () => {
      set.delete(cb)
      if (set.size === 0) this.teardownRealtime(projectId)
    }
  }

  private emit(projectId: string) {
    const set = this.listeners.get(projectId)
    if (!set) return
    const snapshot = this.getSnapshot(projectId)
    set.forEach(l => l(snapshot))
  }

  async refresh(projectId: string, force: boolean = false, includeSteps?: number[] | 'all'): Promise<ProjectSession> {
    // If force is true, clear any cached data and inflight requests
    if (force) {
      this.cache.delete(projectId)
      this.inflightRefresh.delete(projectId)
    }

    if (this.inflightRefresh.has(projectId)) return this.inflightRefresh.get(projectId)!
    const promise = (async () => {
      const res = await fetchProject(projectId, includeSteps)
      const p = res.project

      // Transform dedicated table data into step format
      // Step 0: Documents (Input Materials) - was Step 1
      const step0Data = p?.documents?.length ? {
        documents: p.documents.map(d => ({
          id: d.id,
          title: d.title,
          file_path: d.file_path,
          file_type: d.file_type,
          status: d.status,
          uploaded_at: d.uploaded_at
        }))
      } : null

      // Step 1: Industry Research (NEW)
      const step1Data = p?.industry_research?.research_data ? {
        research_data: p.industry_research.research_data
      } : null

      // Step 2: Business Challenges
      const step2Data = p?.business_challenges?.length ? {
        challenges: p.business_challenges.map(c => ({
          challenge_id: c.challenge_id,
          challenge_name: c.title,
          description: c.description,
          segments: c.segments,
          locus: c.locus,
          importance: c.importance,
          trend: c.trend,
          evidence_source: c.evidence_source,
          related_values: c.related_values,
          assumptions: c.assumptions,
          dependencies: c.dependencies,
          notes: c.notes,
          pest_factors: (c as any).pest_factors || [],
          modified_by: c.metadata?.modified_by || 'ai'
        })),
        strategic_context: {
          narrative: '',
          industry_context: '',
          market_position: '',
          strategic_goals: [],
          assumptions_made: []
        }
      } : null

      // Step 3: Customer Values
      const step3Data = p?.customer_values?.length ? {
        customer_values: p.customer_values.map(v => ({
          value_id: v.value_id,
          label: v.label,
          category: v.category,
          description: v.description,
          segments: v.segments,
          evidence_source: v.evidence_source,
          related_challenges: v.related_challenges,
          assumptions: v.assumptions,
          notes: v.notes,
          modified_by: v.metadata?.modified_by || 'ai'
        }))
      } : null

      // Step 4: Prerequisites (with embedded opposite states)
      const step4Data = p?.prerequisites?.length ? {
        prerequisites: p.prerequisites?.map(pr => ({
          prereq_id: pr.prereq_id,
          value_id: pr.value_id,
          statement: pr.statement,
          essentiality: pr.essentiality,
          evidence_source: pr.evidence_source,
          // Embedded opposite state fields
          opp_transformation: pr.opp_transformation,
          opp_statement: pr.opp_statement,
          opp_rationale: pr.opp_rationale,
          // Summary sentence field
          summary_sentence: (pr as any).summary_sentence,
          notes: pr.notes,
          modified_by: pr.metadata?.modified_by || 'ai',
          metadata: pr.metadata || {}
        })) || []
      } : null

      // Step 5: Ideas
      const step5Data = p?.ideas?.length ? {
        ideas: p.ideas.map(i => ({
          idea_id: i.idea_id,
          value_id: i.value_id,
          title: i.title,
          tagline: i.tagline,
          description: i.description,
          linked_opposite_states: i.linked_opposite_states,
          linked_prerequisites: i.linked_prerequisites,
          linked_business_challenges: i.linked_business_challenges,
          segments: i.segments,
          image_prompt: i.image_prompt,
          dependencies: i.dependencies,
          notes: i.notes,
          modified_by: i.modified_by
        }))
      } : null

      // Step 6: Idea Scores (Idea Evaluation & Ranking)
      const step6Data = p?.idea_scores?.length ? {
        evaluationMetadata: {
          scale: "1-5",
          weights: {
            customer_value_impact: 0.4,
            market_size: 0.3,
            asset_alignment: 0.3
          },
          methodology: "customer_value_driven",
          notes: ""
        },
        ideas: p.idea_scores.map(score => {
          // Find the corresponding idea details
          const ideaDetails = p.ideas?.find(i => i.idea_id === score.idea_id)

          // Parse the detailed scores payload if present
          const scoresData = typeof score.scores === 'string'
            ? safeJsonParse(score.scores) || {}
            : (score.scores || {})

          return {
            idea_id: score.idea_id,
            idea_title: ideaDetails?.title || score.metadata?.idea_title || 'Untitled Idea',
            linked_challenge_ids: ideaDetails?.linked_business_challenges || [],
            segment_labels: ideaDetails?.segments || [],
            // Value proposition is driven by customer_value_impacts
            customer_value_impacts: scoresData.customer_value_impacts || [],
            // Restore full market_size_estimate object from scores JSONB
            market_size_estimate: scoresData.market_size_estimate && typeof scoresData.market_size_estimate === 'object'
              ? scoresData.market_size_estimate
              : {
                  market_type: "Unknown",
                  market_value: "Unknown",
                  market_basis: "",
                  growth_potential: 0,
                  market_rationale: ""
                },
            // Restore full asset_alignment object from scores JSONB
            asset_alignment: scoresData.asset_alignment && typeof scoresData.asset_alignment === 'object' && !Array.isArray(scoresData.asset_alignment)
              ? scoresData.asset_alignment
              : {
                  alignment_score: 0,
                  key_assets_used: [],
                  asset_gaps: [],
                  alignment_rationale: ""
                },
            scores: {
              customer_value_impact: scoresData.customer_value_impact || score.customer_value_impact || 0,
              market_size: scoresData.market_size || score.market_size || 0,
              // asset_alignment score: prefer numeric from scoresData, fallback to score column, then to object's alignment_score
              asset_alignment: typeof scoresData.asset_alignment === 'number'
                ? scoresData.asset_alignment
                : (score.asset_alignment || (scoresData.asset_alignment && typeof scoresData.asset_alignment === 'object' && !Array.isArray(scoresData.asset_alignment) ? scoresData.asset_alignment.alignment_score : 0)),
              total_score: scoresData.total_score || score.total_score || 0
            },
            rationales: scoresData.rationales || {
              customer_value_impact: "",
              market_size: "",
              asset_alignment: ""
            },
            evidence: scoresData.evidence || []
          }
        }),
        rankedShortlist: p.idea_scores?.slice(0, 3).map(score => ({
          idea_id: score.idea_id,
          total_score: score.total_score,
          why_in_top: ""
        })) || [],
        assumptionsMade: [],
        openQuestionsForClient: []
      } : null

      // Step 7: Dimensions and White Space Discovery
      // Include both new dimensions format and legacy axis pairs for backward compatibility
      const pAny = p as any
      const step7Data = (pAny?.dimensions?.length || p?.axis_pairs?.length || pAny?.white_space_candidates?.length) ? {
        // New Step 7 format: dimensions, dimension_scores, white_space_candidates
        dimensions: pAny?.dimensions || [],
        dimension_scores: pAny?.dimension_scores || [],
        white_space_candidates: pAny?.white_space_candidates || [],  // Now loaded with main project data!
        // Legacy format: axis pairs (for backward compatibility)
        axisPairs: p?.axis_pairs?.map(axis => ({
          axis_id: axis.axis_id,
          name: axis.name,
          axis_x_name: axis.axis_x_name,
          axis_y_name: axis.axis_y_name,
          axis_x_description: axis.axis_x_description,
          axis_y_description: axis.axis_y_description,
          source: axis.source,
          composite_score: axis.composite_score,
          separability_score: axis.separability_score,
          concentration_score: axis.concentration_score,
          clarity_score: axis.clarity_score,
          // Include quadrant_definitions with Step 9 evaluation data
          quadrant_definitions: axis.quadrant_definitions || {}
        })) || [],
        allocations_by_axis: p?.allocations_by_axis || {}
      } : null

      // Step 8: White Space Candidates (Unevaluated)
      // Candidates are identified in Step 8 (Phase 3)
      const step8Data = (pAny?.white_space_candidates?.length) ? {
         candidates: pAny.white_space_candidates // Raw candidates
      } : null

      // Step 9: White Space Opportunities (Evaluated Candidates)
      // Filter white_space_candidates to only include those with strategic evaluation
      const step9Data: Step9WhiteSpaceData | null = (pAny?.white_space_candidates?.length) ? {
        evaluatedWhiteSpaces: pAny.white_space_candidates
          .filter((ws: any) => 
            ws.strategic_score !== null && 
            ws.strategic_score !== undefined
          )
          .map((ws: any) => ({
            ...ws,
            // Parse JSON strings if needed (database stores as JSONB but API may return strings)
            risks: typeof ws.risks === 'string' ? 
              (ws.risks ? JSON.parse(ws.risks) : []) : 
              (ws.risks || []),
            opportunity_patterns: typeof ws.opportunity_patterns === 'string' ? 
              (ws.opportunity_patterns ? JSON.parse(ws.opportunity_patterns) : []) : 
              (ws.opportunity_patterns || [])
          })),
        selectedWhiteSpace: (p && p.selected_dimension_x && p.selected_dimension_y && p.selected_quadrant) ? {
          x_dimension_key: p.selected_dimension_x!,
          y_dimension_key: p.selected_dimension_y!,
          quadrant: p.selected_quadrant!
        } : undefined
      } : null

      // Step 10: Refined Ideas (Quadrant Ideas)
      // New schema: quadrant_ideas are individual refined idea rows
      const step10Data = (p?.quadrant_ideas?.length) ? {
        quadrantIdeas: p.quadrant_ideas || []
      } : null

      // Step 11: Business Opportunities (uses summary data)
      const step11Data = (p?.summary && Object.keys(p.summary).length > 0) ? p.summary : null

      const session: ProjectSession = {
        projectId,
        steps: {
          step0: step0Data,
          step1: step1Data,
          step2: step2Data,
          step3: step3Data,
          step4: step4Data,
          step5: step5Data,
          step6: step6Data,
          step7: step7Data,
          step8: step8Data,
          step9: step9Data,
          step10: step10Data,
          step11: step11Data,
        },
        stepFinalizationStatus: p?.step_finalization_status || {},
        updatedAt: p?.updated_at,
        // Store full project details for components
        project: p,
      }

      this.cache.set(projectId, session)
      this.emit(projectId)
      return session
    })()
    this.inflightRefresh.set(projectId, promise)
    try {
      return await promise
    } finally {
      this.inflightRefresh.delete(projectId)
    }
  }

  async getStep(projectId: string, stepId: StepId): Promise<StepData | null> {
    const snap = this.getSnapshot(projectId)
    if (snap) return snap.steps[stepId]
    const refreshed = await this.refresh(projectId)
    return refreshed.steps[stepId]
  }

  async updateStep(projectId: string, stepNumber: number, payload: StepData): Promise<void> {
    const stepKey = stepNumberToKey(stepNumber)
    // optimistic update
    const prev = this.getSnapshot(projectId)
    if (prev) {
      const next: ProjectSession = {
        ...prev,
        steps: { ...prev.steps, [stepKey]: payload },
      }
      this.cache.set(projectId, next)
      this.emit(projectId)
    }

    // Note: updateStepData was removed in favor of dedicated endpoints
    // Step data is now persisted through dedicated CRUD endpoints
    // Refresh to sync with latest server data
    await this.refresh(projectId)
  }

  async deleteStep(projectId: string, stepNumber: number): Promise<void> {
    const stepKey = stepNumberToKey(stepNumber)
    const prev = this.getSnapshot(projectId)
    if (prev) {
      const next: ProjectSession = {
        ...prev,
        steps: { ...prev.steps, [stepKey]: null },
      }
      this.cache.set(projectId, next)
      this.emit(projectId)
    }
    // Note: updateStepData was removed in favor of dedicated endpoints
    // Step data deletion is now handled through dedicated delete endpoints
    // Refresh to sync with latest server data
    await this.refresh(projectId)
  }

  private ensureRealtime(projectId: string) {
    if (!supabase || this.realtimeSubscriptions.has(projectId)) return
    const channel = supabase.channel(`projects:${projectId}`)
    channel.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'projects', filter: `id=eq.${projectId}` },
      () => {
        this.refresh(projectId).catch(() => { })
      }
    )
    // subscribe returns the channel; no promise in current Supabase client
    channel.subscribe()
    this.realtimeSubscriptions.set(projectId, {
      unsubscribe: () => {
        channel.unsubscribe()
      },
    })
  }

  private teardownRealtime(projectId: string) {
    const sub = this.realtimeSubscriptions.get(projectId)
    if (sub) {
      sub.unsubscribe()
      this.realtimeSubscriptions.delete(projectId)
    }
  }

  /**
   * Safely parse JSON without throwing.
   */
}

export const sessionStore = new SessionStore()

function safeJsonParse(value: string): any | null {
  try {
    return JSON.parse(value)
  } catch {
    console.warn('⚠️ Failed to parse JSON from idea_scores.scores payload')
    return null
  }
}


